import type { PresentationDoc } from '@tbcast/shared';
import type { PlaybackContext, Renderable } from './types';

/** The subset of @tbcast/slides-engine the player relies on (see its README). */
interface EngineModule {
  PresentationPlayer: new (
    container: HTMLElement,
    opts: {
      doc: PresentationDoc;
      ctx: {
        now: () => Date;
        timeZone: string;
        locale: string;
        screenName?: string;
        organizationName?: string;
        weatherTemp?: () => number | null;
      };
      resolveAsset: (src: string, mediaId?: string) => string | Promise<string>;
      loop?: boolean;
      muted?: boolean;
      onCycleEnd?: () => void;
    },
  ) => {
    start(): Promise<void>;
    stop(): void;
    destroy(): void;
    resize(w: number, h: number): void;
    readonly totalDurationMs: number;
  };
  requiredFonts(doc: PresentationDoc): string[];
  loadFonts(families: string[]): Promise<void>;
}

let enginePromise: Promise<EngineModule> | null = null;
export function loadEngine(): Promise<EngineModule> {
  enginePromise ??= import('@tbcast/slides-engine') as unknown as Promise<EngineModule>;
  return enginePromise;
}

/** Maps slide asset references to cached asset URLs. */
/**
 * Maps a slide element's media reference to a cached (offline-safe) URL.
 * `src` may be `<mediaId>#page=<n>` for imported PDF/PPTX slides. Blob URLs
 * carry no extension, so video assets (incl. GIF→MP4) get a `#video` hint for
 * the engine's image/video detection.
 */
export function makeResolver(ctx: PlaybackContext) {
  return (src: string, mediaId?: string): string => {
    const pageMatch = /^([^#]+)#page=(\d+)$/.exec(src);
    const id = mediaId ?? pageMatch?.[1];
    if (!id) return src;
    const media = ctx.manifest.media.find((m) => m.id === id);
    if (!media) return src;
    const page = pageMatch ? Number(pageMatch[2]) : null;
    const assetId =
      page !== null && media.pageAssetIds?.length
        ? media.pageAssetIds[Math.min(Math.max(page - 1, 0), media.pageAssetIds.length - 1)]
        : (media.assetId ?? media.pageAssetIds?.[0]);
    const url = assetId ? ctx.cache.url(assetId) : null;
    if (!url) return src;
    const asset = ctx.manifest.assets.find((a) => a.id === assetId);
    return asset?.mimeType.startsWith('video/') ? `${url}#video` : url;
  };
}

export function createPresentationRenderable(
  doc: PresentationDoc,
  ctx: PlaybackContext,
  opts: { loop: boolean },
): Renderable {
  const el = document.createElement('div');
  el.className = 'tb-layer tb-presentation';
  let player: InstanceType<EngineModule['PresentationPlayer']> | null = null;
  let endedCb: (() => void) | null = null;
  let resizeObs: ResizeObserver | null = null;
  let destroyed = false;
  return {
    el,
    async ready() {
      const engine = await loadEngine();
      await engine.loadFonts(engine.requiredFonts(doc)).catch(() => undefined);
      if (destroyed) return;
      player = new engine.PresentationPlayer(el, {
        doc,
        ctx: {
          now: () => new Date(),
          timeZone: ctx.timeZone,
          locale: ctx.locale,
          screenName: ctx.manifest.screen.name,
          organizationName: ctx.manifest.organization.name,
          weatherTemp: ctx.weatherTemp,
        },
        resolveAsset: makeResolver(ctx),
        loop: opts.loop,
        muted: ctx.volume <= 0,
        onCycleEnd: () => endedCb?.(),
      });
    },
    get intrinsicDurationMs() {
      return player?.totalDurationMs;
    },
    play() {
      if (!player) return;
      const rect = el.getBoundingClientRect();
      player.resize(rect.width || window.innerWidth, rect.height || window.innerHeight);
      resizeObs = new ResizeObserver(([entry]) => {
        if (entry) player?.resize(entry.contentRect.width, entry.contentRect.height);
      });
      resizeObs.observe(el);
      void player.start();
    },
    onEnded(cb) {
      endedCb = cb;
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      resizeObs?.disconnect();
      player?.destroy();
      player = null;
      el.remove();
    },
  };
}
